import React, { useState } from "react";
import { Student, GraduationCap, CaretDown, BookOpen } from "@phosphor-icons/react";

/**
 * DualExplainer
 * -------------
 * Eine Dual-View-Info-Box für Datenseiten, die zwei Zielgruppen
 * gleichzeitig bedient:
 *   1. **Bauer-Ansicht** (immer sichtbar)  — alltagssprachliche Erklärung
 *      mit Praxis-Tipp und konkretem Nutzen.
 *   2. **Wissenschaft-Ansicht** (ausklappbar) — Formeln, Einheiten,
 *      Datenquellen, Interpretationshilfen.
 *
 * Props:
 *   - title:   Titel der Info-Box
 *   - simple:  JSX für die Bauer-Erklärung
 *   - science: JSX für die wissenschaftliche Erklärung
 *   - testid:  Test-ID-Präfix
 *
 * Beispiel:
 *   <DualExplainer
 *     title="Was ist NDVI?"
 *     simple={<p>Der NDVI zeigt, wie grün deine Felder gerade sind ...</p>}
 *     science={<p>NDVI = (NIR − Rot) / (NIR + Rot), Sentinel-2, 10 m ...</p>}
 *   />
 */
export default function DualExplainer({ title, simple, science, testid = "dual-explainer" }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="eid-card p-4 md:p-5 bg-[#0A0A0A] border-l-4 border-[#00FA9A]/40 space-y-3"
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <span className="shrink-0 p-2 rounded-sm bg-[#00FA9A]/10 text-[#00FA9A]">
          <BookOpen size={18} weight="fill" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 inline-flex items-center gap-1">
              <Student size={11} /> Für Landwirte
            </span>
          </div>
          <h3 className="text-base font-medium tracking-tight mt-0.5">{title}</h3>
          <div
            className="text-sm text-white mt-1 leading-relaxed space-y-2"
            data-testid={`${testid}-simple`}
          >
            {simple}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 pt-2">
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid={`${testid}-toggle`}
          className="w-full text-left inline-flex items-center justify-between gap-2 text-xs font-mono uppercase tracking-wider text-zinc-400 hover:text-white transition-colors"
        >
          <span className="inline-flex items-center gap-1.5">
            <GraduationCap size={13} />
            Wissenschaftliche Details {open ? "schließen" : "anzeigen"}
          </span>
          <CaretDown
            size={12}
            style={{ transform: open ? "rotate(180deg)" : "rotate(0)" }}
            className="transition-transform"
          />
        </button>
        {open && (
          <div
            className="mt-3 text-sm text-white leading-relaxed space-y-2 bg-[#121212] border border-white/10 rounded-sm p-3"
            data-testid={`${testid}-science`}
          >
            {science}
          </div>
        )}
      </div>
    </div>
  );
}
